import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { track } from '@/lib/analytics';
import { fetchApi } from '@/lib/api';
import { Footer, MarketingHeader, SEO } from '@/components';
import {
  FiCheckCircle,
  FiClipboard,
  FiFileText,
  FiMail,
  FiMessageSquare,
  FiBriefcase,
  FiUserPlus,
} from 'react-icons/fi';

declare global {
  interface Window {
    gtagSendEvent: (eventName: string) => void;
    fbq: (...args: unknown[]) => void;
  }
}

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
} as const;

const AUDIENCE = 'lawyers' as const;

const handleProSignUpClick = (location: string) => {
  track('Click Sign Up', { location, audience: AUDIENCE });
  if (typeof window.gtagSendEvent === 'function') {
    window.gtagSendEvent('click_sign_up');
  }
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'Lead');
  }
};

const handleContactEmailClick = (location: string) => {
  track('Click Contact Email', { location, audience: AUDIENCE });
};

const handleSupportSMSClick = (location: string) => {
  track('Click Support SMS', { location, audience: AUDIENCE });
};

const handleLeaveBehindClick = (location: string) => {
  track('Click Footer Link', { link: 'Learn for Lawyers', location, audience: AUDIENCE });
};

const formatPhoneNumber = (phone: string): string => {
  const digits = phone.replace(/\D/g, '').replace(/^1/, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

interface SupportInfoResponse {
  phone_number: string;
}

const REPORTS = [
  {
    id: 'compliance',
    icon: FiClipboard,
    title: 'Parenting Time Compliance Report',
    description:
      'A dated summary of logged exchanges and schedule adherence—what was recorded, when, and by whom—so you can see gaps without rebuilding a binder from texts.',
    details: [
      'Exchange totals and lateness stats with timestamps',
      'Solo vs paired household provenance on the face of the report',
      'Exportable PDF your client can send you in one tap',
    ],
  },
  {
    id: 'disputes',
    icon: FiFileText,
    title: 'Expense Dispute Summary',
    description:
      'Open and resolved disputes in one place: amounts, unpaid balances, receipts, and status—ready for negotiation or hearing prep.',
    details: [
      'Dispute status, amounts, and unpaid balances',
      'Supporting receipt context when logged',
      'Clear enough for counsel without a spreadsheet rebuild',
    ],
  },
  {
    id: 'hearing',
    icon: FiBriefcase,
    title: 'Hearing Prep Brief',
    description:
      'A curated brief that pulls compliance, disputes, and a chronological fact timeline into one counsel-facing PDF before court.',
    details: [
      'Cover + table of contents for quick navigation',
      'Compliance and dispute sections in one packet',
      'Chronological timeline for hearing prep',
    ],
  },
] as const;

function HeroSection() {
  return (
    <section className='bg-primary/5 pt-32 pb-16'>
      <div className='max-w-4xl mx-auto px-6 text-center'>
        <p
          className='text-primary font-semibold text-sm uppercase tracking-wide mb-3'
          style={{ ...nunito, fontWeight: 600 }}
        >
          Harbor for family lawyers
        </p>
        <h1
          className='text-4xl md:text-5xl font-bold text-gray-900 mb-5'
          style={{ ...nunito, fontWeight: 700 }}
        >
          When the other parent won&apos;t join, your client still gets a record
          you can use.
        </h1>
        <p
          className='text-xl text-gray-600 max-w-2xl mx-auto mb-4'
          style={{ ...nunito, fontWeight: 400 }}
        >
          Harbor works solo or paired. Clients document parenting time, expenses,
          and messages; you get prep artifacts built for counsel—not another app
          pitch for both parents to adopt.
        </p>
        <p
          className='text-base text-gray-500 max-w-xl mx-auto mb-8'
          style={{ ...nunito, fontWeight: 400 }}
        >
          One parent can start today. If the other joins later, the household
          becomes paired—without throwing away the record already built.
        </p>
        <div className='flex flex-col sm:flex-row items-center justify-center gap-3'>
          <Link
            to='/auth/pro/sign-up'
            onClick={() => handleProSignUpClick('Learn Practitioners Hero')}
            className='inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-6 rounded-lg transition-colors w-full sm:w-auto'
            style={{ ...nunito, fontWeight: 600 }}
          >
            <FiUserPlus className='w-5 h-5' />
            Create free pro account
          </Link>
          <Link
            to='/learn/lawyers'
            onClick={() => handleLeaveBehindClick('Learn Practitioners Hero')}
            className='inline-flex items-center justify-center gap-2 border-2 border-primary text-primary hover:bg-primary/5 font-semibold py-3 px-6 rounded-lg transition-colors w-full sm:w-auto'
            style={{ ...nunito, fontWeight: 600 }}
          >
            One-page leave-behind
          </Link>
        </div>
      </div>
    </section>
  );
}

function ReportsSection() {
  return (
    <section className='py-20 bg-sky-light'>
      <div className='max-w-5xl mx-auto px-6'>
        <div className='text-center mb-12'>
          <span
            className='text-primary font-semibold text-lg mb-2 block'
            style={{ ...nunito, fontWeight: 600 }}
          >
            Prep artifacts for counsel
          </span>
          <h2
            className='text-3xl md:text-4xl font-bold text-gray-900 mb-4'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Three reports your client can send you
          </h2>
          <p
            className='text-lg text-gray-600 max-w-2xl mx-auto'
            style={{ ...nunito, fontWeight: 400 }}
          >
            Built for hearing weeks and negotiation—not marketing claims about
            platform exclusivity.
          </p>
        </div>

        <div className='space-y-8'>
          {REPORTS.map((report) => {
            const Icon = report.icon;
            return (
              <article
                key={report.id}
                className='bg-white rounded-2xl border border-gray-200 p-8'
              >
                <div className='flex flex-col md:flex-row md:items-start gap-6'>
                  <div className='w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0'>
                    <Icon className='w-7 h-7 text-primary' />
                  </div>
                  <div className='flex-1'>
                    <h3
                      className='text-2xl font-bold text-gray-900 mb-3'
                      style={{ ...nunito, fontWeight: 700 }}
                    >
                      {report.title}
                    </h3>
                    <p
                      className='text-lg text-gray-600 mb-5 leading-relaxed'
                      style={{ ...nunito, fontWeight: 400 }}
                    >
                      {report.description}
                    </p>
                    <ul className='space-y-2'>
                      {report.details.map((detail) => (
                        <li key={detail} className='flex items-start gap-3'>
                          <FiCheckCircle className='w-5 h-5 text-primary flex-shrink-0 mt-0.5' />
                          <span
                            className='text-gray-600'
                            style={{ ...nunito, fontWeight: 400 }}
                          >
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function InviteCodesSection() {
  return (
    <section className='bg-white py-20'>
      <div className='max-w-4xl mx-auto px-6'>
        <div className='text-center mb-10'>
          <h2
            className='text-3xl md:text-4xl font-bold text-gray-900 mb-4'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Invite codes for clients
          </h2>
          <p
            className='text-lg text-gray-600 max-w-2xl mx-auto'
            style={{ ...nunito, fontWeight: 400 }}
          >
            Sign up as a professional, get invite codes, and hand a client a link
            that starts their trial and can share exports with your practice.
          </p>
        </div>

        <ol className='space-y-6 mb-10'>
          {[
            {
              step: '1',
              title: 'Create a free pro account',
              body: 'Register at the Harbor pro portal—no co-parent invite required.',
            },
            {
              step: '2',
              title: 'Copy an invite code or link',
              body: 'Each code starts a client trial and can link their household back to you.',
            },
            {
              step: '3',
              title: 'Client documents; you download reports',
              body: 'When they share access, pull Parenting Time Compliance, Expense Dispute Summary, or Hearing Prep Brief from the pro dashboard.',
            },
          ].map((item) => (
            <li key={item.step} className='flex gap-4'>
              <span
                className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-white font-bold'
                style={{ ...nunito, fontWeight: 700 }}
              >
                {item.step}
              </span>
              <div>
                <h3
                  className='text-xl font-bold text-gray-900 mb-1'
                  style={{ ...nunito, fontWeight: 700 }}
                >
                  {item.title}
                </h3>
                <p className='text-gray-600' style={{ ...nunito, fontWeight: 400 }}>
                  {item.body}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <div className='flex flex-col sm:flex-row items-center justify-center gap-3'>
          <Link
            to='/auth/pro/sign-up'
            onClick={() => handleProSignUpClick('Learn Practitioners Invite CTA')}
            className='inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-6 rounded-lg transition-colors w-full sm:w-auto'
            style={{ ...nunito, fontWeight: 600 }}
          >
            <FiUserPlus className='w-5 h-5' />
            Get invite codes
          </Link>
          <Link
            to='/learn/lawyers'
            onClick={() => handleLeaveBehindClick('Learn Practitioners Invite CTA')}
            className='inline-flex items-center justify-center text-primary hover:underline font-semibold py-3 px-2'
            style={{ ...nunito, fontWeight: 600 }}
          >
            Printable leave-behind for counsel →
          </Link>
        </div>

        <p
          className='mt-12 text-center text-sm text-gray-500'
          style={{ ...nunito, fontWeight: 400 }}
        >
          Client subscriptions start at $9.99/month or $49.99/year after a 7-day trial.
          Exports to counsel stay available without extra documentation fees.
        </p>
      </div>
    </section>
  );
}

function ContactSection() {
  const supportQuery = useQuery({
    queryKey: ['support-info'],
    queryFn: () => fetchApi<SupportInfoResponse>('/v1/support-info'),
    staleTime: 1000 * 60 * 60,
  });

  const supportPhone = supportQuery.data?.phone_number ?? '+14157469059';

  return (
    <section className='bg-primary py-16'>
      <div className='max-w-4xl mx-auto px-6'>
        <div className='text-center mb-10'>
          <h2
            className='text-3xl md:text-4xl font-bold text-white mb-3'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Questions from counsel?
          </h2>
          <p
            className='text-lg text-white/80'
            style={{ ...nunito, fontWeight: 400 }}
          >
            Ask about invite codes, report exports, or a short walkthrough for
            your practice.
          </p>
        </div>

        <div className='grid md:grid-cols-2 gap-6'>
          <a
            href='mailto:team@harborcoparent.com'
            onClick={() => handleContactEmailClick('Learn Practitioners')}
            className='bg-white/10 hover:bg-white/20 rounded-2xl p-6 transition-colors'
          >
            <div className='w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4'>
              <FiMail className='w-6 h-6 text-white' />
            </div>
            <h3
              className='text-xl font-bold text-white mb-2'
              style={{ ...nunito, fontWeight: 700 }}
            >
              Email Us
            </h3>
            <p className='text-white/80 mb-3' style={{ ...nunito, fontWeight: 400 }}>
              Partnership or client onboarding questions.
            </p>
            <span className='text-white font-medium'>team@harborcoparent.com →</span>
          </a>

          <a
            href={`sms:${supportPhone}`}
            onClick={() => handleSupportSMSClick('Learn Practitioners')}
            className='bg-white/10 hover:bg-white/20 rounded-2xl p-6 transition-colors'
          >
            <div className='w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4'>
              <FiMessageSquare className='w-6 h-6 text-white' />
            </div>
            <h3
              className='text-xl font-bold text-white mb-2'
              style={{ ...nunito, fontWeight: 700 }}
            >
              Text Support
            </h3>
            <p className='text-white/80 mb-3' style={{ ...nunito, fontWeight: 400 }}>
              Support available 24/7 — text us anytime.
            </p>
            <span className='text-white font-medium'>
              Text {formatPhoneNumber(supportPhone)} →
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

function LearnPractitionersPage() {
  useEffect(() => {
    track('Learn Practitioners Screen', { audience: AUDIENCE });
  }, []);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title="Harbor for Family Lawyers | Prep Artifacts When One Parent Won't Join"
        description="When the other parent won't join, your client still gets a record counsel can use. Solo or paired. Parenting Time Compliance Report, Expense Dispute Summary, and Hearing Prep Brief—plus pro invite codes."
        path='/learn/practitioners'
      />
      <MarketingHeader />
      <HeroSection />
      <ReportsSection />
      <InviteCodesSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/learn/practitioners')({
  component: LearnPractitionersPage,
});
